/**
 * Sentry posts: the places a guard would sensibly be put - the north side of every
 * crossing, the bends of the road, inside the gates, beside each target, the compound's
 * corners. The budget draws on these before it falls back to open ground.
 */

import { HALF_PI } from '../sim/math';
import type { Pcg32 } from '../sim/rng';
import { FLAG } from './grid';
import { NO_STAND } from './builder';
import type { GenCtx, Point, Post } from './types';

export function collectPosts(ctx: GenCtx): Post[] {
  const posts: Post[] = [];
  const { plan, bands, sizeX } = ctx;
  const inward = (x: number): number => (x < sizeX / 2 ? 1 : -1);
  const push = (x: number, z: number, facing: number, band: number, weight: number): void => {
    posts.push({ x, z, facing, band, weight, used: false });
  };

  plan.lines.forEach((line, i) => {
    // Posts by the first line stand well back: most of the drop band must be free of enemies (03 §5.2).
    const back = i === 0 ? 13 : 5;
    push(line.roadX + 3.5 * inward(line.roadX), line.z - back, 0, i + 1, i === 0 ? 1 : 3);
    push(line.quietX + 6 * inward(line.quietX), line.z - back - 2, 0, i + 1, i === 0 ? 0.5 : 2);
    if (line.gaps.some((g) => g.x === line.midX)) push(line.midX + 3, line.z - back, 0, i + 1, 1.5);
    const next = plan.lines[i + 1];
    if (next) push((line.roadX + next.roadX) / 2 + 4 * inward(line.roadX), (line.z + next.z) / 2, 0, i + 1, 1.5);
  });

  const goal = bands.length - 1;
  for (const g of plan.gates) {
    if (g.side === 'south') push(g.x + 4, g.z - 4, 0, goal, 3);
    else push(g.x + (g.side === 'west' ? 4 : -4), g.z + 3.5, g.side === 'west' ? -HALF_PI : HALF_PI, goal, 2);
  }
  for (const prop of ctx.map.props) {
    if (prop.target !== true) continue;
    const b = ctx.map.bounds(prop.kind, prop.x, prop.z, prop);
    push(prop.x + 1.5, b.z1 + 2.4, 0, goal, 3);
  }
  const c = plan.compound;
  if (ctx.spec.patrolLoop === 'inside') {
    for (const x of [c.x0 + 5, c.x1 - 5]) for (const z of [c.z1 - 5, c.z0 + 5]) push(x, z, 0, goal, 1);
  }
  return posts;
}

/** Take the best unused post in a band, resolved to a free cell; null when the band has none left. */
export function takePost(ctx: GenCtx, rng: Pcg32, band: number): { x: number; z: number; facing: number } | null {
  const candidates = ctx.posts.filter((p) => !p.used && p.band === band);
  let total = 0;
  for (const p of candidates) total += p.weight;
  while (candidates.length > 0 && total > 0) {
    let roll = rng.float() * total;
    let index = 0;
    for (let i = 0; i < candidates.length; i++) {
      roll -= candidates[i]?.weight ?? 0;
      if (roll < 0) {
        index = i;
        break;
      }
    }
    const [post] = candidates.splice(index, 1);
    if (!post) break;
    total -= post.weight;
    post.used = true;
    const spot = ctx.map.nearestOpen(post.x, post.z, 3, NO_STAND | FLAG.keep);
    if (spot) return { x: spot[0], z: spot[1], facing: post.facing };
  }
  return null;
}

/** A free spot in open ground somewhere in the band, away from the ranger's start and the pickup. */
export function openGround(ctx: GenCtx, rng: Pcg32, band: number, from?: Point, within = 0): Point | null {
  const b = ctx.bands[band];
  if (!b) return null;
  const { player, pickup } = ctx.plan;
  // Only the northern part of the drop band is ever garrisoned.
  const z1 = b.kind === 'drop' ? b.z0 + (b.z1 - b.z0) * 0.35 : b.z1 - 3;
  for (let tries = 0; tries < 30; tries++) {
    const x = from ? from.x + rng.range(-within, within) : rng.range(6, ctx.sizeX - 6);
    const z = from ? from.z + rng.range(-within, within) : rng.range(Math.max(4, b.z0 + 3), z1);
    const far = (p: Point, d: number): boolean => (p.x - x) * (p.x - x) + (p.z - z) * (p.z - z) > d * d;
    if (!far(player, 28) || !far(pickup, 12)) continue;
    const spot = ctx.map.nearestOpen(x, z, 2, NO_STAND | FLAG.keep | FLAG.road);
    if (spot) return { x: spot[0], z: spot[1] };
  }
  return null;
}
